import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, apiErr } from "@/lib/api";
import { firstLengthError } from "@/lib/fieldLimits";
import { useLang } from "@/context/LanguageContext";
import { PageHeader } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { UserPlus, Check, Layers, Building2, Copy, ChevronLeft, ChevronRight, CalendarClock } from "lucide-react";
import { Step1Start } from "@/components/onboard/Step1Start";
import { Step2Attributes } from "@/components/onboard/Step2Attributes";
import { Step3Access } from "@/components/onboard/Step3Access";
import { Step4Review } from "@/components/onboard/Step4Review";
import { RecipientPicker } from "@/components/onboard/RecipientPicker";
import { byLastName } from "@/lib/format";

const MBX_BADGE = {
  room: { label: "Room", cls: "border-violet-300 bg-violet-50 text-violet-700" },
  equipment: { label: "Equipment", cls: "border-amber-300 bg-amber-50 text-amber-700" },
  shared: { label: "Shared", cls: "border-zinc-300 bg-zinc-100 text-zinc-600" },
};
const mbxBadge = (box) => MBX_BADGE[(box.userPurpose || box.mailboxType || "shared").toLowerCase()] || MBX_BADGE.shared;

const STEPS = [
  { n: 1, label: "onboard.step1", hint: "onboard.step1hint", icon: Copy },
  { n: 2, label: "onboard.step2", hint: "onboard.step2hint", icon: Building2 },
  { n: 3, label: "onboard.step3", hint: "onboard.step3hint", icon: Layers },
  { n: 4, label: "onboard.step4", hint: "onboard.step4hint", icon: Check },
];

const ID_FIELDS = [
  ["givenName", "field.givenName", true], ["surname", "field.surname", true],
  ["officeLocation", "field.officeNumber", false], ["mobilePhone", "field.mobile", false],
  ["streetAddress", "field.street", false], ["postalCode", "field.zip", false], ["city", "field.city", false],
];

function applyPattern(pattern, first, last) {
  const f = (first || "").trim();
  const l = (last || "").trim();
  return (pattern || "{firstname}.{lastname}")
    .replace(/\{firstname\}/gi, f)
    .replace(/\{lastname\}/gi, l)
    .replace(/\{f\}/gi, f.slice(0, 1))
    .replace(/\{l\}/gi, l.slice(0, 1))
    .replace(/\s+/g, "")
    .toLowerCase();
}

// Display-name pattern: preserves original case and single spaces between tokens.
function applyDisplayPattern(pattern, first, last) {
  const f = (first || "").trim();
  const l = (last || "").trim();
  return (pattern || "{firstname} {lastname}")
    .replace(/\{firstname\}/gi, f)
    .replace(/\{lastname\}/gi, l)
    .replace(/\{f\}/gi, f.slice(0, 1))
    .replace(/\{l\}/gi, l.slice(0, 1))
    .replace(/\s+/g, " ")
    .trim();
}

export default function Onboard() {
  const nav = useNavigate();
  const { t } = useLang();
  const [groups, setGroups] = useState([]);
  const [licenses, setLicenses] = useState([]);
  const [teams, setTeams] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [sharedMbx, setSharedMbx] = useState([]);
  const [settings, setSettings] = useState(null);
  const [locations, setLocations] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [orgUsers, setOrgUsers] = useState([]);
  const [leaved, setLeaved] = useState([]);
  const [domains, setDomains] = useState([]);
  const [upnTouched, setUpnTouched] = useState(false);
  const [samTouched, setSamTouched] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [upnAvailable, setUpnAvailable] = useState(null);
  const [upnReason, setUpnReason] = useState(null);
  const [upnChecking, setUpnChecking] = useState(false);
  const [samAvailable, setSamAvailable] = useState(null);
  const [samSource, setSamSource] = useState(null);
  const [samChecking, setSamChecking] = useState(false);
  const [emailTouched, setEmailTouched] = useState(false);
  const [emailState, setEmailState] = useState({ checking: false, available: null, reason: null });
  const [cloneId, setCloneId] = useState("");
  const [cloning, setCloning] = useState(false);
  const [apps, setApps] = useState([]);
  const [step, setStep] = useState(1);
  const [recipients, setRecipients] = useState([]);
  const [passwordRecipients, setPasswordRecipients] = useState([]);
  const [scheduleEnabled, setScheduleEnabled] = useState(false);
  const [scheduleFor, setScheduleFor] = useState("");
  const [cfg, setCfg] = useState(null);
  const [seatByGroup, setSeatByGroup] = useState({});

  const [f, setF] = useState({
    givenName: "", surname: "", jobTitle: "", department: "", officeLocation: "",
    streetAddress: "", postalCode: "", city: "",
    mobilePhone: "", companyName: "", usageLocation: "US", manager: "", managerName: "",
    upnPrefix: "", samAccountName: "", domain: "", email: "", password: "", forceChange: true, roleTemplateId: "", description: "", hiddenFromGal: false,
  });
  const [sel, setSel] = useState({ groups: [], dls: [], licenses: [], teams: [], shared: [], apps: [] });

  useEffect(() => {
    Promise.all([api.get("/groups"), api.get("/licenses"), api.get("/teams"), api.get("/role-templates")])
      .then(([g, l, te, r]) => { setGroups(g.data.filter((x) => x.isVisible !== false)); setLicenses(l.data); setTeams(te.data.filter((x) => x.isVisible !== false)); setTemplates(r.data); });
    api.get("/applications").then((r) => setApps(r.data)).catch(() => {});
    api.get("/group-license-usage").then((r) => {
      const map = {};
      (r.data || []).forEach((g) => { map[g.groupId] = { minAvailable: g.minAvailable, skus: g.skus }; });
      setSeatByGroup(map);
    }).catch(() => {});
    api.get("/users").then((r) => setOrgUsers(r.data)).catch(() => {});
    api.get("/leaved-employees").then((r) => setLeaved(r.data)).catch(() => {});
    api.get("/config").then((r) => setCfg(r.data)).catch(() => {});
    api.get("/recipients").then((r) => setRecipients(r.data)).catch(() => {});
    api.get("/users/shared").then((r) => setSharedMbx(r.data.filter((x) => x.isVisible !== false))).catch(() => {});
    api.get("/auth/me").then(() => {});
    api.get("/usage-locations").then((r) => setLocations(r.data)).catch(() => {});
    // settings is admin-only; onboarders may be 'user' role -> fall back to defaults
    Promise.all([
      api.get("/companies").then((r) => r.data).catch(() => []),
      api.get("/domains").then((r) => r.data).catch(() => []),
      api.get("/settings").then((r) => r.data).catch(() => null),
    ]).then(([cos, doms, st]) => {
      setCompanies(cos);
      setDomains(doms);
      if (st) setSettings(st);
      const tenantDefault = (doms.find((d) => d.isDefault) || {}).id || "";
      // Default company = explicitly marked default, else the only company if just one exists
      const defCo = cos.find((c) => c.isDefault) || (cos.length === 1 ? cos[0] : null);
      setF((p) => ({
        ...p,
        usageLocation: (st && st.defaultUsageLocation) || p.usageLocation,
        companyName: defCo ? defCo.name : p.companyName,
        domain: (defCo && defCo.defaultDomain) || tenantDefault || p.domain,
        ...(defCo ? addrFrom(defCo) : {}),
      }));
    });
    generate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // auto-fill UPN prefix from pattern unless manually overridden
  useEffect(() => {
    if (upnTouched) return;
    const pattern = settings?.upnPattern || "{firstname}.{lastname}";
    setF((p) => ({ ...p, upnPrefix: applyPattern(pattern, p.givenName, p.surname) }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [f.givenName, f.surname, settings, upnTouched]);

  // auto-fill AD sAMAccountName from the UPN prefix unless manually overridden
  useEffect(() => {
    if (samTouched) return;
    setF((p) => ({ ...p, samAccountName: p.upnPrefix }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [f.upnPrefix, samTouched]);

  // auto-fill primary E-Mail from UPN (prefix@domain) unless manually overridden
  useEffect(() => {
    if (emailTouched) return;
    if (f.upnPrefix && f.domain) setF((p) => ({ ...p, email: `${p.upnPrefix}@${p.domain}` }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [f.upnPrefix, f.domain, emailTouched]);

  // debounced email domain + duplicate validation
  useEffect(() => {
    if (!f.email || !f.email.includes("@")) { setEmailState({ checking: false, available: null, reason: null }); return; }
    setEmailState((s) => ({ ...s, checking: true }));
    const target = f.email;
    const h = setTimeout(() => {
      api.get(`/validate/email?email=${encodeURIComponent(target)}`)
        .then((r) => setEmailState({ checking: false, available: r.data.available, reason: r.data.reason }))
        .catch(() => setEmailState({ checking: false, available: null, reason: null }));
    }, 400);
    return () => clearTimeout(h);
  }, [f.email]);

  const generate = async () => {
    try { const { data } = await api.post("/utils/generate-password"); setF((p) => ({ ...p, password: data.password })); }
    catch { setF((p) => ({ ...p, password: "Welcome@123!" })); }
  };

  const secGroups = groups.filter((g) => !["cloud_distribution", "cloud_teams", "licensing", "m365_group"].includes(g.classification) && !g.dynamicMembership && !g.dynamic && !g.applicationId);
  const m365Groups = groups.filter((g) => g.classification === "m365_group" && !g.dynamicMembership && !g.dynamic && !g.applicationId);
  const distGroups = groups.filter((g) => g.classification === "cloud_distribution" && !g.dynamicMembership && !g.dynamic && !g.applicationId);
  const licenseGroups = groups.filter((g) => g.classification === "licensing" && !g.dynamicMembership && !g.applicationId);
  // Onboard seat guard: selected licensing groups whose SKU pool has no free seats (block before start).
  const licGroupIds = new Set(licenseGroups.map((g) => g.id));
  const noSeatGroups = sel.groups.filter((id) => licGroupIds.has(id)
    && seatByGroup[id] && typeof seatByGroup[id].minAvailable === "number" && seatByGroup[id].minAvailable <= 0);
  const dynamicGroups = groups.filter((g) => (g.dynamicMembership || g.dynamic) && !g.applicationId && g.classification !== "cloud_teams");
  const appOpts = apps.map((a) => ({ ...a, displayName: a.name }));
  const upn = `${(f.upnPrefix || "user").toLowerCase()}@${f.domain}`;
  const upnValid = /^[a-zA-Z0-9._-]+$/.test(f.upnPrefix);
  const displayName = applyDisplayPattern(settings?.displayNamePattern, f.givenName, f.surname);
  const cloudOnly = settings?.operationMode === "cloudOnly";
  const tapActive = cloudOnly && !!settings?.tap?.enabled;
  const deliveryConfigured = ["toManager", "toCreator", "toFixed", "toManual"].some((k) => settings?.passwordDelivery?.[k]);

  // Live UPN-availability check (debounced) so the wizard blocks a duplicate before onboarding.
  useEffect(() => {
    if (!f.upnPrefix || !upnValid || !f.domain) { setUpnAvailable(null); return; }
    setUpnChecking(true);
    const target = upn;
    const h = setTimeout(() => {
      api.get(`/validate/upn?upn=${encodeURIComponent(target)}`)
        .then((r) => { setUpnAvailable(r.data.available); setUpnReason(r.data.reason || null); })
        .catch(() => { setUpnAvailable(null); setUpnReason(null); })
        .finally(() => setUpnChecking(false));
    }, 400);
    return () => clearTimeout(h);
  }, [upn, upnValid, f.upnPrefix, f.domain]);

  // Live sAMAccountName-availability check (debounced) — catches AD sAMAccountName duplicates
  // (across the on-prem inventory snapshot + Entra cache) before onboarding dispatches to AD.
  useEffect(() => {
    if (cloudOnly || !f.samAccountName) { setSamAvailable(null); setSamSource(null); return; }
    setSamChecking(true);
    const target = f.samAccountName;
    const h = setTimeout(() => {
      api.get(`/validate/sam?sam=${encodeURIComponent(target)}`)
        .then((r) => { setSamAvailable(r.data.available); setSamSource(r.data.source || null); })
        .catch(() => { setSamAvailable(null); setSamSource(null); })
        .finally(() => setSamChecking(false));
    }, 400);
    return () => clearTimeout(h);
  }, [f.samAccountName, cloudOnly]);

  const toggle = (cat, id) => setSel((s) => ({ ...s, [cat]: s[cat].includes(id) ? s[cat].filter((x) => x !== id) : [...s[cat], id] }));

  const cloneOptions = useMemo(() => [
    ...orgUsers.map((u) => ({ id: u.id, displayName: u.displayName, userPrincipalName: u.userPrincipalName, surname: u.surname, leaved: false })),
    ...leaved.map((u) => ({ id: u.id, displayName: u.displayName, userPrincipalName: u.userPrincipalName, surname: u.surname, leaved: true })),
  ].sort(byLastName), [orgUsers, leaved]);
  const leavedIds = useMemo(() => new Set(leaved.map((u) => u.id)), [leaved]);

  const [params] = useSearchParams();
  useEffect(() => {
    const c = params.get("clone");
    if (c) setCloneId(c);
  }, [params]);

  const cloneFrom = async () => {
    if (!cloneId) return;
    setCloning(true);
    try {
      const src = leavedIds.has(cloneId) ? `/leaved-employees/${cloneId}` : `/users/${cloneId}`;
      const { data: u } = await api.get(src);
      const memberOf = u.memberOf || [];
      const teamIds = u.teamMemberships || [];
      const lic = u.assignedLicenses || [];
      const secIds = new Set([...secGroups, ...m365Groups, ...licenseGroups].map((g) => g.id));
      const dlIds = new Set(distGroups.map((g) => g.id));
      const teamSet = new Set(teams.map((t2) => t2.id));
      const licSet = new Set(licenses.map((l) => l.skuId));
      const gSel = memberOf.filter((id) => secIds.has(id));
      const dSel = memberOf.filter((id) => dlIds.has(id));
      const tSel = teamIds.filter((id) => teamSet.has(id));
      const lSel = lic.filter((id) => licSet.has(id));
      setSel((s) => ({ ...s, groups: gSel, dls: dSel, teams: tSel, licenses: lSel }));
      toast.success(t("onb.cloned"), {
        description: `${gSel.length + dSel.length} ${t("onb.sumGroups").toLowerCase()} · ${tSel.length} ${t("onb.sumTeams").toLowerCase()} · ${lSel.length} ${t("onb.sumLicenses").toLowerCase()}`,
      });
    } catch (e) { toast.error(apiErr(e)); } finally { setCloning(false); }
  };

  const toggleShared = (box) => setSel((s) => {
    const exists = s.shared.find((x) => x.id === box.id);
    return { ...s, shared: exists ? s.shared.filter((x) => x.id !== box.id)
      : [...s.shared, { id: box.id, mail: box.mail || box.userPrincipalName, access: "fullAccess" }] };
  });
  const setSharedAccess = (id, access) => setSel((s) => ({ ...s, shared: s.shared.map((x) => x.id === id ? { ...x, access } : x) }));

  const applyTemplate = (rid) => {
    const tm = templates.find((x) => x.id === rid);
    setF((p) => ({ ...p, roleTemplateId: rid, department: tm?.department || p.department, jobTitle: tm?.defaultJobTitle || p.jobTitle, companyName: tm?.companyName || p.companyName, manager: tm?.defaultManager || p.manager, managerName: tm?.defaultManagerName || p.managerName, usageLocation: tm?.usageLocation || p.usageLocation }));
    if (tm) setSel({ groups: [...tm.groups], dls: [...tm.distributionLists], licenses: [...tm.licenses], teams: [...tm.teams], shared: [...(tm.sharedMailboxes || [])], apps: [...(tm.applications || [])] });
  };

  const selCompany = companies.find((c) => c.name === f.companyName);
  const addrFrom = (src) => {
    const o = {};
    if (src?.streetAddress) o.streetAddress = src.streetAddress;
    if (src?.postalCode) o.postalCode = src.postalCode;
    if (src?.city) o.city = src.city;
    return o;
  };
  const tenantDefaultDomain = () => (domains.find((d) => d.isDefault) || {}).id || "";
  const pickCompany = (name) => {
    const c = companies.find((x) => x.name === name);
    setF((p) => ({ ...p, companyName: name, ...addrFrom(c),
      ...(c ? { domain: c.defaultDomain || tenantDefaultDomain() || p.domain } : {}) }));
  };
  const pickDept = (name) => {
    const d = selCompany?.departments.find((x) => x.name === name);
    setF((p) => ({ ...p, department: name,
      ...(d ? { manager: d.managerId || "", managerName: d.managerName || "" } : {}),
      ...addrFrom(d) }));
  };
  const pickManager = (id) => {
    if (id === "__none__") { setF((p) => ({ ...p, manager: "", managerName: "" })); return; }
    const u = orgUsers.find((x) => x.id === id);
    setF((p) => ({ ...p, manager: id, managerName: u?.displayName || "" }));
  };

  const refName = (list, id, key = "id") => list.find((x) => x[key] === id)?.displayName || id;
  const summary = useMemo(() => ({
    groups: sel.groups.map((id) => refName(groups, id)),
    dls: sel.dls.map((id) => refName(groups, id)),
    licenses: sel.licenses.map((id) => refName(licenses, id, "skuId")),
    teams: sel.teams.map((id) => refName(teams, id)),
    apps: sel.apps.map((id) => apps.find((a) => a.id === id)?.name || id),
    shared: sel.shared.map((s) => `${refName(sharedMbx, s.id)} (${s.access === "sendAs" ? "Send As" : "Full Access"})`),
  }), [sel, groups, licenses, teams, sharedMbx, apps]);

  const submit = async () => {
    if (!f.givenName || !f.surname) { toast.error(t("onb.nameRequired")); return; }
    if (!f.upnPrefix || !upnValid) { toast.error(t("onb.upnInvalid")); return; }
    if (upnAvailable === false) { toast.error(t("onb.upnTaken")); setStep(2); return; }
    if (!f.email || !f.email.includes("@")) { toast.error(t("onb.emailRequired")); setStep(2); return; }
    if (emailState.available === false) { toast.error(t("onb.emailInvalid")); setStep(2); return; }
    if (!cloudOnly && !f.samAccountName) { toast.error(t("onb.samRequired")); setStep(2); return; }
    if (!cloudOnly && samAvailable === false) { toast.error(t("onb.samTaken")); setStep(2); return; }
    if (!reqOk) { toast.error(t("onb.reqFieldsMissing")); setStep(2); return; }
    if (tapActive && !deliveryConfigured) { toast.error(t("onb.tapNeedsDelivery")); return; }
    if (noSeatGroups.length > 0) {
      const names = noSeatGroups.map((id) => refName(groups, id)).join(", ");
      toast.error(t("onb.noSeatsBlock"), { description: names });
      setStep(3);
      return;
    }
    const lenErr = firstLengthError([
      [t("field.givenName"), f.givenName, "givenName"],
      [t("field.surname"), f.surname, "surname"],
      [t("field.company"), f.companyName, "companyName"],
      [t("field.department"), f.department, "department"],
      [t("field.jobTitle"), f.jobTitle, "jobTitle"],
      [t("field.officeNumber"), f.officeLocation, "officeLocation"],
      [t("field.mobile"), f.mobilePhone, "mobilePhone"],
      [t("field.street"), f.streetAddress, "streetAddress"],
      [t("field.zip"), f.postalCode, "postalCode"],
      [t("field.city"), f.city, "city"],
      [t("onb.upnPrefix"), f.upnPrefix, "upnPrefix"],
      [t("field.samAccountName"), f.samAccountName, "samAccountName"],
      [t("field.email"), f.email, "email"],
      [t("field.description"), f.description, "description"],
    ]);
    if (lenErr) { toast.error(lenErr); setStep(2); return; }
    let scheduledIso = null;
    if (scheduleEnabled) {
      if (!scheduleFor) { toast.error(t("onb.schedulePickDate")); return; }
      const when = new Date(scheduleFor);
      if (isNaN(when.getTime()) || when.getTime() <= Date.now() + 60000) { toast.error(t("onb.scheduleFuture")); return; }
      scheduledIso = when.toISOString();
    }
    setSubmitting(true);
    try {
      const mailNickname = f.email.includes("@") ? f.email.split("@")[0].toLowerCase() : f.upnPrefix;
      const managerName = (orgUsers.find((u) => u.id === f.manager) || {}).displayName || "";
      const configSummary = {
        identity: {
          displayName, userPrincipalName: upn, jobTitle: f.jobTitle, department: f.department,
          company: f.companyName, manager: managerName, usageLocation: f.usageLocation,
          office: f.officeLocation, mobilePhone: f.mobilePhone, city: f.city,
        },
        groups: summary.groups, dls: summary.dls, licenses: summary.licenses,
        teams: summary.teams, shared: summary.shared, apps: summary.apps,
      };
      const body = {
        attrs: { displayName, givenName: f.givenName, surname: f.surname, userPrincipalName: upn,
          mailNickname, jobTitle: f.jobTitle, department: f.department, companyName: f.companyName,
          officeLocation: f.officeLocation, mobilePhone: f.mobilePhone, usageLocation: f.usageLocation,
          streetAddress: f.streetAddress, postalCode: f.postalCode, city: f.city,
          ...(f.description ? { description: f.description } : {}),
          ...(f.manager ? { manager: f.manager } : {}),
          ...(f.email ? { mail: f.email } : {}),
          ...(!cloudOnly && f.samAccountName ? { samAccountName: f.samAccountName } : {}) },
        password: f.password, forceChangePassword: f.forceChange, hiddenFromGal: !!f.hiddenFromGal, roleTemplateId: f.roleTemplateId || null,
        groups: sel.groups, distributionLists: sel.dls, licenses: sel.licenses, teams: sel.teams,
        applications: sel.apps, sharedMailboxes: sel.shared, summary: configSummary,
        passwordRecipients,
        ...(scheduledIso ? { scheduledFor: scheduledIso } : {}),
      };
      const { data } = await api.post("/users", body);
      if (data && data.scheduled) {
        toast.success(t("onb.scheduled"), { description: `${displayName} · ${new Date(data.scheduledFor).toLocaleString()}` });
        nav("/jobs");
      } else if (data && data.waiting) {
        toast.warning(t("onb.queuedLicense"), { description: data.message });
        nav("/jobs");
      } else {
        toast.success(t("onb.onboarded"), { description: `${displayName} created · createUser dispatched.` });
        nav("/");
      }
    } catch (e) { toast.error(apiErr(e)); } finally { setSubmitting(false); }
  };

  const reqFields = settings?.requiredFields || cfg?.requiredFields || [];
  const fieldFilled = (k) => k === "manager" ? (!!f.manager && f.manager !== "__none__") : !!String(f[k] || "").trim();
  const reqOk = reqFields.every(fieldFilled);
  const REQ_LABELS = { companyName: "field.company", department: "field.department", jobTitle: "field.jobTitle", manager: "field.manager", mobilePhone: "field.mobile", officeLocation: "field.officeNumber", streetAddress: "field.street", postalCode: "field.zip", city: "field.city" };
  const missingReq = [
    ...(!f.givenName ? ["field.givenName"] : []),
    ...(!f.surname ? ["field.surname"] : []),
    ...(!f.upnPrefix || !upnValid ? ["onb.upnPrefix"] : []),
    ...(!f.email || !f.email.includes("@") ? ["field.email"] : []),
    ...((!cloudOnly && !f.samAccountName) ? ["field.samAccountName"] : []),
    ...reqFields.filter((k) => !fieldFilled(k)).map((k) => REQ_LABELS[k]).filter(Boolean),
  ].map((k) => t(k));
  const canStep2 = !!f.givenName && !!f.surname && !!f.upnPrefix && upnValid && upnAvailable !== false
    && !!f.email && f.email.includes("@") && emailState.available !== false && (cloudOnly || !!f.samAccountName)
    && reqOk;
  const goStep = (n) => { if (n > 2 && !canStep2) { toast.error(t("onb.nameRequired")); setStep(2); return; } setStep(n); };

  return (
    <>
      <PageHeader title={t("onboard.title")} subtitle={t("onboard.subtitle")} />
      <div className="mx-auto max-w-5xl p-6 md:p-10">
        <div className="mb-8 grid grid-cols-4 gap-2" data-testid="onboard-stepper">
          {STEPS.map((s) => {
            const active = step === s.n;
            const done = step > s.n;
            return (
              <button key={s.n} type="button" onClick={() => goStep(s.n)} data-testid={`onboard-step-${s.n}`}
                className={`flex items-center gap-3 border-b-2 px-2 py-3 text-left transition-colors ${active ? "border-primary" : done ? "border-emerald-400" : "border-zinc-200 hover:border-zinc-300"}`}>
                <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-bold ${active ? "bg-primary text-white" : done ? "bg-emerald-500 text-white" : "bg-zinc-100 text-zinc-400"}`}>
                  {done ? <Check size={15} /> : s.n}
                </span>
                <span className="hidden min-w-0 sm:block">
                  <span className={`block truncate text-sm font-bold ${active ? "text-zinc-900" : "text-zinc-500"}`}>{t(s.label)}</span>
                  <span className="block truncate text-[11px] text-zinc-400">{t(s.hint)}</span>
                </span>
              </button>
            );
          })}
        </div>

        <div className="border border-zinc-200 bg-white p-6 md:p-8" data-testid={`onboard-panel-${step}`}>
          {step === 1 && <Step1Start t={t} orgUsers={cloneOptions} cloneId={cloneId} setCloneId={setCloneId} cloneFrom={cloneFrom} cloning={cloning} templates={templates} f={f} applyTemplate={applyTemplate} />}
          {step === 2 && <Step2Attributes t={t} companies={companies} selCompany={selCompany} f={f} setF={setF} pickCompany={pickCompany} pickDept={pickDept} orgUsers={orgUsers} pickManager={pickManager} ID_FIELDS={ID_FIELDS} upnTouched={upnTouched} setUpnTouched={setUpnTouched} upnValid={upnValid} domains={domains} locations={locations} generate={generate} upnAvailable={upnAvailable} upnChecking={upnChecking} upnReason={upnReason} cloudOnly={cloudOnly} samTouched={samTouched} setSamTouched={setSamTouched} samAvailable={samAvailable} samSource={samSource} samChecking={samChecking} emailTouched={emailTouched} setEmailTouched={setEmailTouched} emailState={emailState} reqFields={reqFields} cloudDescEnabled={!!cfg?.cloudUserDescription?.enabled} tapActive={tapActive} />}
          {step === 3 && <Step3Access t={t} appOpts={appOpts} sel={sel} toggle={toggle} secGroups={secGroups} m365Groups={m365Groups} dynamicGroups={dynamicGroups} distGroups={distGroups} licenseGroups={licenseGroups} licenses={licenses} teams={teams} sharedMbx={sharedMbx} toggleShared={toggleShared} setSharedAccess={setSharedAccess} mbxBadge={mbxBadge} hiddenFromGal={f.hiddenFromGal} setHiddenGal={(v) => setF((p) => ({ ...p, hiddenFromGal: v }))} seatByGroup={seatByGroup} noSeatGroups={noSeatGroups} groupName={(id) => refName(groups, id)} />}
          {step === 4 && <Step4Review t={t} displayName={displayName} upn={upn} f={f} summary={summary} />}
          {step === 4 && (
            <div className="mt-6 border-t border-zinc-100 pt-5" data-testid="onboard-schedule">
              <label className="flex cursor-pointer items-center gap-2.5">
                <input type="checkbox" checked={scheduleEnabled} onChange={(e) => setScheduleEnabled(e.target.checked)} data-testid="onboard-schedule-toggle" className="h-4 w-4 rounded border-zinc-300 text-primary focus:ring-primary" />
                <span className="flex items-center gap-2 text-sm font-bold text-zinc-800"><CalendarClock size={16} className="text-primary" /> {t("onb.scheduleToggle")}</span>
              </label>
              <p className="ml-6.5 mt-1 text-xs text-zinc-400">{t("onb.scheduleHint")}</p>
              {scheduleEnabled && (
                <div className="mt-3 max-w-xs">
                  <input type="datetime-local" value={scheduleFor} min={new Date(Date.now() + 60000).toISOString().slice(0, 16)}
                    onChange={(e) => setScheduleFor(e.target.value)} data-testid="onboard-schedule-datetime"
                    className="w-full rounded-sm border border-zinc-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary" />
                </div>
              )}
            </div>
          )}
          {step === 4 && settings?.passwordDelivery?.toManual && (
            <div className="mt-6 border-t border-zinc-100 pt-5" data-testid="onboard-password-recipients">
              <div className="mb-2 text-xs font-bold uppercase tracking-wider text-zinc-500">{t("onb.pwRecipients")}</div>
              <p className="mb-3 text-xs text-zinc-400">{t("onb.pwRecipientSingleHint")}</p>
              <div className="max-w-md">
                <RecipientPicker t={t} recipients={recipients}
                  value={passwordRecipients[0] || ""}
                  onChange={(email) => setPasswordRecipients(email ? [email] : [])}
                  placeholder={t("set.pwPickRecipient")} />
              </div>
            </div>
          )}
        </div>

        <div className="mt-6 flex items-center justify-between">
          <Button variant="ghost" onClick={() => setStep((s) => Math.max(1, s - 1))} disabled={step === 1} data-testid="onboard-back" className="rounded-sm">
            <ChevronLeft size={16} className="mr-1" /> Back
          </Button>
          <div className="flex flex-col items-end gap-1.5">
            {step === 2 && !canStep2 && missingReq.length > 0 && (
              <p className="text-xs text-amber-600" data-testid="onboard-missing-hint">{t("onb.stillNeeded")}: {missingReq.join(", ")}</p>
            )}
            {step === 4 && noSeatGroups.length > 0 && (
              <p className="text-xs font-semibold text-red-600" data-testid="onboard-noseat-hint">{t("onb.noSeatsHint")}</p>
            )}
            {step < 4 ? (
              <Button onClick={() => goStep(step + 1)} disabled={step === 2 && !canStep2} data-testid="onboard-next" className="rounded-sm bg-primary px-6 hover:bg-[#002480]">
                Next <ChevronRight size={16} className="ml-1" />
              </Button>
            ) : (
              <Button onClick={submit} disabled={submitting || !canStep2 || noSeatGroups.length > 0} data-testid="onboard-submit" className="h-11 rounded-sm bg-primary px-6 text-sm font-semibold uppercase tracking-wide hover:bg-[#002480]">
                <UserPlus size={16} className="mr-2" /> {submitting ? t("onb.provisioning") : (scheduleEnabled ? t("onb.scheduleCreate") : t("onboard.create"))}
              </Button>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
